import { useMutation } from '@apollo/client'
import { ButtonType } from '@src/components/Button'
import { LoaderCheck } from '@src/components/Check/LoaderCheck'
import { SelectInput, TextInput } from '@src/components/Inputs'
import { AlertModal } from '@src/components/Modal/AlertModal'
import { useCustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { MainRouteName, useAccountRouter } from '@src/hooks/useAccountRouter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import ReactPixel from 'react-facebook-pixel'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { customerSupportEmailDocument } from './mutations/__generated__/customerSupportEmail.graphql-interface'
import { OrderNumberInput } from './OrderNumberInput'
import {
  FAQsText,
  HelpQuestion,
  SubmitButton,
  SupportContainer,
} from './SupportFormStyle'
import { useSupportForm } from './useSupportForm'

export const SupportForm: React.FC = () => {
  const { t } = useTranslation('supportForm')
  const [showError, setShowError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isOrderVerified, setIsOrderVerified] = useState(false)
  const [currentSubject, setCurrentSubject] = useState('')
  const { setRoute } = useAccountRouter()
  const { id: marketplaceId } = useMarketplace()
  const [searchParams] = useSearchParams()
  const { validateOrderExists, getSupportFormValidation } = useSupportForm()
  const { data } = useCustomerDetailsAndAddressesQuery()
  const prefilledOrderNumber = searchParams.get('orderNumber') || ''
  const customer = data?.customerDetails

  const initialValues = {
    subject: prefilledOrderNumber
      ? t('subjectOptions.order_issues')
      : t('subjectOptions.order_issues'),
    firstName: customer?.firstName || '',
    lastName: customer?.lastName || '',
    emailAddress: customer?.email || '',
    phoneNumber: customer?.phoneNumber || '',
    orderNumber: prefilledOrderNumber,
    message: '',
  }

  useEffect(() => {
    const isOrderIssues = currentSubject === t('subjectOptions.order_issues')
    if (!isOrderIssues) {
      setIsOrderVerified(false)
    }
  }, [currentSubject, t])

  const handleOrderValidation = async (orderNumber: string) => {
    // Normalize order number: trim and remove a leading '#'
    const normalizedOrderNumber = orderNumber.trim().replace(/^#/, '')
    if (!normalizedOrderNumber) {
      setIsOrderVerified(false)
      return { outletName: undefined, isValid: false }
    }

    try {
      const orderDetails = await validateOrderExists(normalizedOrderNumber)
      const isValid = !!orderDetails
      setIsOrderVerified(isValid)
      return {
        outletName: orderDetails?.outlet.displayName,
        isValid,
      }
    } catch {
      setIsOrderVerified(false)
      return { outletName: undefined, isValid: false }
    }
  }

  const subjectOptions = [
    {
      value: t('subjectOptions.order_issues'),
      name: t('subjectOptions.order_issues'),
    },
    {
      value: t('subjectOptions.payment_issues'),
      name: t('subjectOptions.payment_issues'),
    },
    {
      value: t('subjectOptions.account_questions'),
      name: t('subjectOptions.account_questions'),
    },
    {
      value: t('subjectOptions.website_questions'),
      name: t('subjectOptions.website_questions'),
    },
    {
      value: t('subjectOptions.something_else'),
      name: t('subjectOptions.something_else'),
    },
  ]

  const [sendSupportEmail] = useMutation(customerSupportEmailDocument, {
    onError() {
      setShowError(true)
    },
    onCompleted() {
      setShowSuccess(true)
      ReactPixel.track('Contact')
    },
  })

  return (
    <>
      {showSuccess ? (
        <AlertModal
          isOpen={showSuccess}
          title={t('submit.status.success')}
          subTitle={
            <>
              <LoaderCheck />
              {t('submit.status.success_content')}
            </>
          }
          action={{
            text: t('Close'),
            intent: ButtonType.PRIMARY,
            onClick: () => {
              setRoute({
                mainRouteName: MainRouteName.MENU,
                childRouteName: undefined,
              })
              setShowSuccess(false)
            },
          }}
          isLeftSidebar={true}
        />
      ) : (
        <SupportContainer>
          <HelpQuestion>{t('help_question')}</HelpQuestion>
          <FAQsText>{t('faq')}</FAQsText>
          <Formik
            initialValues={initialValues}
            validationSchema={toFormikValidationSchema(
              getSupportFormValidation(currentSubject)
            )}
            validateOnChange
            validateOnBlur={true}
            onSubmit={async (values, actions) => {
              await sendSupportEmail({
                variables: {
                  marketplaceId,
                  ...values,
                  // Normalize order number for submission (remove optional leading '#')
                  orderNumber: values.orderNumber.trim().replace(/^#/, ''),
                },
              })
              actions.resetForm({ values: initialValues })
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => {
              const isOrderIssues =
                values.subject === t('subjectOptions.order_issues')
              const canSubmit =
                !isOrderIssues || (isOrderIssues && isOrderVerified)

              return (
                <Form>
                  <SelectInput
                    name="subject"
                    label={t('subject.label')}
                    options={subjectOptions}
                    required
                    onChange={e => {
                      setFieldValue('subject', e.value)
                      setCurrentSubject(e.value)
                      if (e.value !== t('subjectOptions.order_issues')) {
                        setFieldValue('orderNumber', '')
                        setIsOrderVerified(false)
                      }
                    }}
                  />
                  <TextInput
                    name="firstName"
                    label={t('first_name.label')}
                    required
                    autoComplete="given-name"
                  />
                  <TextInput
                    name="lastName"
                    label={t('last_name.label')}
                    required
                    autoComplete="family-name"
                  />
                  <TextInput
                    name="emailAddress"
                    label={t('email_address.label')}
                    required
                    autoComplete="email"
                  />
                  <TextInput
                    name="phoneNumber"
                    label={t('phone_number.label')}
                    required
                  />

                  {isOrderIssues && (
                    <OrderNumberInput
                      name="orderNumber"
                      label={t('order_number.label')}
                      required={isOrderIssues}
                      placeholder="Order Number"
                      onOrderValidate={handleOrderValidation}
                      onValidationChange={setIsOrderVerified}
                    />
                  )}

                  <TextInput
                    name="message"
                    label={t('message.label')}
                    required
                    isTextArea={true}
                  />
                  <SubmitButton
                    type="submit"
                    content={t('submit.label')}
                    disabled={!canSubmit}
                    loading={isSubmitting}
                  />
                </Form>
              )
            }}
          </Formik>
          <AlertModal
            isOpen={showError}
            title={t('submit.status.error')}
            subTitle={t('submit.status.error_content')}
            action={{
              text: t('ok'),
              intent: ButtonType.DANGER,
              onClick: () => setShowError(false),
            }}
            isLeftSidebar={true}
          />
        </SupportContainer>
      )}
    </>
  )
}
